import { seasons, groups, suppliers, contactPersons, brandOptions } from './stylesFixtureData';
import type { StyleRow } from './stylesFixtureData';

/**
 * Dedicated, self-contained fixtures for the DL-66548 epic (Testmo folder 93166 part 5 of 5,
 * dl-57699 — "Styles bulk edit MVP: Step 4 apply, the activity log, and the Styles-table
 * regression"). Real, persisted `stylesData` rows (not an ephemeral `apiTransform` override) so a
 * bulk PUT against them round-trips through the same find-by-id / merge / persist path every other
 * fixture uses, and the table's post-"Apply changes" refetch genuinely reflects the new values —
 * an `apiTransform`-only override would freeze the response at a fixed snapshot and could never
 * show a post-update refetch or a re-sort.
 *
 * Every set shares a unique name substring (absent from every other fixture) so a spec selects
 * exactly its own rows via the table's own search box rather than an index into a virtualised row
 * list. All rows are in an editable state (`unpublished`) so the Edit button enables and the
 * floating selection bar shows. Ids are in the 97300–97399 band, disjoint from every other
 * bulk-edit / styles fixture (94xxx feedback, 96510–96515 preview-confirm-log, 96600–96699 sort,
 * 93xxx performance, 972xx) — an id collision would let a bulk PUT's find-by-id hit a different
 * row than the one a spec selected by name.
 *
 * Set-by-set:
 *  - `P5ApplyMulti A/B` (97300/97301) — the "apply several General fields at once" story (Testmo
 *    #754872): both start on a Season and Group that a bulk-set to AW26 / Womenswear genuinely
 *    changes, so a single Apply must move BOTH fields on BOTH rows.
 *  - `P5ApplySingle` (97302) — the single-style full-flow story (Testmo #754874): one row, so the
 *    modal exercises every singular string (`1 selected`, `Edit 1 style`, a one-item Impacted list).
 *  - `P5FilterReapply A/B` (97320/97321) — the "filter still applies after apply" story (Testmo
 *    #754865): edited on Season (NOT part of the name/number search predicate) so the rows stay
 *    matching the active search and the post-apply refetch must re-run that filter and still show
 *    the new value.
 *  - `P5SortReapply Alfa/Bravo/Charlie` (97310/97311/97312) — the "sort re-applied after apply"
 *    story (Testmo #754864): seasons chosen so an ascending Season sort orders them
 *    Charlie(AW26) < Bravo(SS25) < Alfa(SS26); editing Charlie's Season to SS26 must move it to the
 *    END of the sorted set on refetch — a client that patched the row in place would leave it in
 *    the wrong position, which only a position assertion catches.
 *  - `P5Regress *` (97330–97334) — the Styles-table regression trio (Testmo #754868/#754869/#754870):
 *    a read-only set (no test in that spec applies a change) with distinct Seasons/Groups so a
 *    Season sort visibly reorders them, and enough rows that a bottom-anchored floating bar could
 *    plausibly overlap the last one.
 *  - `P5AuditIso Alfa/Bravo` (97340/97341) + `P5AuditAdd One` (97342) — the activity-log stories
 *    (Testmo #754853 per-style isolation, #754856 custom-field entry, #754854 added-from-empty): the
 *    Iso pair each carries a DISTINCT initial "Fabric Notes" (CF 701) value; Add starts empty. The
 *    two search tokens (`P5AuditIso` → 2 rows, `P5AuditAdd` → 1) keep the pair and the add-row
 *    separately selectable. These are collision-free equivalents of the
 *    DL-66477 `PCLProbe Log*` fixtures, whose ids (96510/96511) collide with a second fixture file
 *    on develop (see the epic PR's "Blocked / bugs found").
 */

type SeasonRef = (typeof seasons)[number];
type GroupRef = (typeof groups)[number];

const AW25 = seasons[0];
const AW26 = seasons[1];
const SS25 = seasons[2];
const SS26 = seasons[3];

const MENSWEAR = groups[0];
const WOMENSWEAR = groups[1];
const KIDSWEAR = groups[2];

/** The baseline non-mandatory "Fabric Notes" text CF (id 701, already in `styleCustomFieldDefinitions`
 * — see styles.ts). A `null` value means the field starts empty (for the "added" audit story). */
const FABRIC_NOTES_CF_ID = 701;
const fabricNotes = (value: string | null): StyleRow['customFields'][number] => ({
  id: FABRIC_NOTES_CF_ID,
  type: 'text',
  value: value === null ? null : { id: 0, value },
});

const base = (
  id: number,
  name: string,
  season: SeasonRef,
  group: GroupRef,
  customFields: StyleRow['customFields'] = []
): StyleRow => ({
  id,
  name,
  customId: `ST-${id}`,
  description: '',
  state: 'unpublished',
  logo: '',
  season,
  group,
  brand: {
    id: brandOptions[0].id,
    customId: brandOptions[0].userDefinedId ?? '',
    name: brandOptions[0].name,
    contactPersonId: contactPersons[0].id,
    contactPersonName: contactPersons[0].name,
  },
  supplier: {
    id: suppliers[0].id,
    customId: suppliers[0].customId,
    name: suppliers[0].name,
    contactPersonId: contactPersons[1].id,
    contactPersonName: contactPersons[1].name,
  },
  categories: [],
  styleColors: [],
  customFields,
  properties: [],
  lastUpdated: '2026-01-01T00:00:00',
  readyForExport: false,
});

export const buildBulkEditApplyRegressionFixtureStyles = (): StyleRow[] => [
  // Multi-field apply (Testmo #754872) — Season SS25/SS26 + Group Menswear; bulk-set both to
  // AW26 + Womenswear so a single Apply is a genuine change on every field of both rows.
  base(97300, 'P5ApplyMulti A', SS25, MENSWEAR),
  base(97301, 'P5ApplyMulti B', SS26, MENSWEAR),

  // Single-style full flow (Testmo #754874) — one row; Season SS25 so bulk-set to AW26 is real.
  base(97302, 'P5ApplySingle One', SS25, MENSWEAR),

  // Sort re-applied after apply (Testmo #754864) — Season sort asc orders these
  // Charlie(AW26) < Bravo(SS25) < Alfa(SS26); editing Charlie → SS26 moves it to the end.
  base(97310, 'P5SortReapply Alfa', SS26, MENSWEAR),
  base(97311, 'P5SortReapply Bravo', SS25, MENSWEAR),
  base(97312, 'P5SortReapply Charlie', AW26, MENSWEAR),

  // Filter re-applied after apply (Testmo #754865) — Group Menswear, bulk-set to Womenswear
  // (Group is not part of the name/number search predicate, so the rows stay in the filtered set).
  base(97320, 'P5FilterReapply A', SS25, MENSWEAR),
  base(97321, 'P5FilterReapply B', SS25, MENSWEAR),

  // Styles-table regression trio (Testmo #754868/#754869/#754870) — read-only set with distinct
  // Seasons/Groups so a Season sort visibly reorders them.
  base(97330, 'P5Regress Alpha', SS26, MENSWEAR),
  base(97331, 'P5Regress Bravo', AW26, WOMENSWEAR),
  base(97332, 'P5Regress Charlie', SS25, KIDSWEAR),
  base(97333, 'P5Regress Delta', AW26, MENSWEAR),
  base(97334, 'P5Regress Echo', SS25, WOMENSWEAR),

  // Activity-log stories (Testmo #754853 per-style isolation, #754856 custom-field entry,
  // #754854 added-from-empty) — each row carries a DISTINCT initial "Fabric Notes" CF value so a
  // leaked/reused (non-per-id) audit response would surface the wrong style's prior value. Alfa/Bravo
  // start with a value (updated-CF + isolation); Added starts empty (the "added from empty" delta).
  base(97340, 'P5AuditIso Alfa', SS25, MENSWEAR, [fabricNotes('Alfa fabric 97340')]),
  base(97341, 'P5AuditIso Bravo', SS26, MENSWEAR, [fabricNotes('Bravo fabric 97341')]),
  base(97342, 'P5AuditAdd One', SS25, MENSWEAR, [fabricNotes(null)]),
];
